import { FC, useCallback, useEffect, useMemo, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  makeStyles,
} from "@material-ui/core"
import { BigCalendar, type CalendarEvent } from "@ksana/vira-ui"
import {
  ClientFragment,
  useNudgesScheduleByUserQuery,
  useQuestionnairesScheduleByUserQuery,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useSession } from "../../hooks/useSession"
import { isNudgeSchedule } from "../../utils/helpers"
import {
  getItemDataFromScheduleItem,
  noChangeAllowed,
} from "../../utils/scheduleUtils"
import {
  fetchTranscriptSessions,
  type TranscriptSessionSummary,
} from "../../utils/transcriptApi"
import { colors } from "../../utils/theme"
import { ScheduleItem } from "../../utils/types"
import CloseIcon from "../atoms/CloseIcon"
import ScheduleItemModal from "./ScheduleItemModal"

type ClientCalendarProps = {
  client: ClientFragment
  practitionerId?: string
  /** When false, skip transcript session fetch. */
  includeSessions?: boolean
  /** Client connected / allowed to schedule (same as ScheduleOverview). */
  canSchedule?: boolean
  onSessionClick?: (sessionId: string) => void
}

function formatTimeMeta(iso: string): string {
  return dayjs(iso).format("h:mm A")
}

function occursOnRecurringDay(item: ScheduleItem, date: Dayjs): boolean {
  switch (date.day()) {
    case 0:
      return Boolean(item.recurring_sunday)
    case 1:
      return Boolean(item.recurring_monday)
    case 2:
      return Boolean(item.recurring_tuesday)
    case 3:
      return Boolean(item.recurring_wednesday)
    case 4:
      return Boolean(item.recurring_thursday)
    case 5:
      return Boolean(item.recurring_friday)
    case 6:
      return Boolean(item.recurring_saturday)
    default:
      return false
  }
}

function isBlackedOut(item: ScheduleItem, date: Dayjs): boolean {
  return Boolean(
    item.schedule_blackout_dates?.find((d) => {
      const blackout = dayjs(d.blackout_date)
      return (
        blackout.isSame(date, "day") ||
        (d.blackout_following && date.isAfter(blackout, "day"))
      )
    })
  )
}

/** Event ids look like `sched-{numericId}-{YYYY-MM-DD}`. */
export function parseScheduleEventId(
  eventId: string
): { scheduleId: number; dateKey: string } | null {
  const match = /^sched-(\d+)-(\d{4}-\d{2}-\d{2})$/.exec(eventId)
  if (!match) return null
  return { scheduleId: Number(match[1]), dateKey: match[2] }
}

function occurrenceAt(item: ScheduleItem, dateKey: string): Dayjs {
  const startAt = dayjs(item.scheduled_at)
  return dayjs(dateKey)
    .hour(startAt.hour())
    .minute(startAt.minute())
    .second(0)
    .millisecond(0)
}

/**
 * Future occurrence that has not already been delivered (no take) and is not
 * a system-account lock — same rules as Schedule overview Edit.
 */
export function isScheduleOccurrenceEditable(
  item: ScheduleItem,
  dateKey: string
): boolean {
  if (noChangeAllowed(item)) return false
  return occurrenceAt(item, dateKey).isAfter(dayjs())
}

/**
 * Expand one-time + recurring schedule rows across the visible month grid
 * (including trailing/leading days from adjacent months).
 */
export function expandScheduleEvents(
  rawSchedule: ScheduleItem[],
  rangeStart: Dayjs,
  rangeEnd: Dayjs
): CalendarEvent[] {
  const events: CalendarEvent[] = []
  const oneTime = rawSchedule.filter((item) => item.one_time)
  const recurring = rawSchedule.filter((item) => !item.one_time)

  for (const item of oneTime) {
    const at = dayjs(item.scheduled_at)
    if (at.isBefore(rangeStart, "day") || at.isAfter(rangeEnd, "day")) continue
    const data = getItemDataFromScheduleItem(item)
    events.push({
      id: `sched-${item.id}-${at.format("YYYY-MM-DD")}`,
      title: data.name,
      start: at.toISOString(),
      kind: isNudgeSchedule(item) ? "nudge" : "assessment",
      meta: formatTimeMeta(item.scheduled_at),
    })
  }

  let cursor = rangeStart.startOf("day")
  const last = rangeEnd.startOf("day")
  while (cursor.isBefore(last) || cursor.isSame(last, "day")) {
    const dateKey = cursor.format("YYYY-MM-DD")
    for (const item of recurring) {
      const startAt = dayjs(item.scheduled_at)
      if (startAt.isAfter(cursor, "day")) continue
      if (!occursOnRecurringDay(item, cursor)) continue
      if (isBlackedOut(item, cursor)) continue
      const data = getItemDataFromScheduleItem(item)
      const occurrence = cursor
        .hour(startAt.hour())
        .minute(startAt.minute())
        .second(0)
      events.push({
        id: `sched-${item.id}-${dateKey}`,
        title: data.name,
        start: occurrence.toISOString(),
        kind: isNudgeSchedule(item) ? "nudge" : "assessment",
        meta: formatTimeMeta(occurrence.toISOString()),
      })
    }
    cursor = cursor.add(1, "day")
  }

  return events
}

function sessionEvents(sessions: TranscriptSessionSummary[]): CalendarEvent[] {
  return sessions.map((session) => {
    const at = dayjs(session.createdAt)
    return {
      id: `session-${session.id}`,
      title: session.title?.trim() || "Therapy session",
      start: at.toISOString(),
      kind: "session" as const,
      meta: formatTimeMeta(session.createdAt),
    }
  })
}

const useViewStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: theme.spacing(2),
  },
  title: {
    padding: theme.spacing(4, 4, 1),
    paddingRight: theme.spacing(6),
  },
  content: {
    padding: theme.spacing(1, 4, 4),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  label: {
    color: colors.neutral.asphalt,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  value: {
    color: colors.neutral.denim,
    fontSize: 14,
  },
  note: {
    marginTop: theme.spacing(1),
    color: colors.neutral.asphalt,
    fontSize: 13,
    lineHeight: 1.5,
  },
}))

function ScheduleEventViewDialog({
  open,
  onClose,
  item,
  dateKey,
}: {
  open: boolean
  onClose: () => void
  item: ScheduleItem
  dateKey: string
}) {
  const classes = useViewStyles()
  const data = getItemDataFromScheduleItem(item)
  const at = occurrenceAt(item, dateKey)
  const kindLabel = isNudgeSchedule(item) ? "Nudge" : "Assessment"
  const past = !at.isAfter(dayjs())
  const locked = noChangeAllowed(item)

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <IconButton className={classes.closeButton} onClick={onClose}>
        <CloseIcon />
      </IconButton>
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h3" component="h2">
          {data.name}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <div>
          <Typography className={classes.label}>Type</Typography>
          <Typography className={classes.value}>{kindLabel}</Typography>
        </div>
        <div>
          <Typography className={classes.label}>When</Typography>
          <Typography className={classes.value}>
            {at.format("dddd, MMM D, YYYY · h:mm A")}
            {!item.one_time ? " · Recurring" : ""}
          </Typography>
        </div>
        {data.message ? (
          <div>
            <Typography className={classes.label}>Message</Typography>
            <Typography className={classes.value}>{data.message}</Typography>
          </div>
        ) : null}
        <Typography className={classes.note}>
          {past
            ? "This occurrence is in the past and can’t be edited."
            : locked
            ? "This item can’t be edited (already delivered or system-managed)."
            : "Scheduling is disabled for this client."}
        </Typography>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Client Detail calendar pane — month view of nudges, assessments, and
 * transcript sessions. Click a future nudge/assessment to edit it.
 */
const ClientCalendar: FC<ClientCalendarProps> = ({
  client,
  practitionerId,
  includeSessions = true,
  canSchedule = true,
  onSessionClick,
}) => {
  const graphqlClient = useGraphQLClient()
  const { session } = useSession()
  const jwt = session?.jwt || ""

  const [month, setMonth] = useState(() => dayjs().startOf("month").toDate())
  const [sessions, setSessions] = useState<TranscriptSessionSummary[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [editItem, setEditItem] = useState<ScheduleItem | undefined>()
  const [viewItem, setViewItem] = useState<ScheduleItem | undefined>()
  const [viewDateKey, setViewDateKey] = useState<string | null>(null)

  const { data: nudgesData, isLoading: loadingNudges } =
    useNudgesScheduleByUserQuery(graphqlClient, {
      user_id: client.id,
    })
  const { data: questionnairesData, isLoading: loadingQuestionnaires } =
    useQuestionnairesScheduleByUserQuery(graphqlClient, {
      user_id: client.id,
    })

  const rawSchedule = useMemo(
    () => [
      ...(nudgesData?.nudge_schedules ?? []),
      ...(questionnairesData?.questionnaire_schedules ?? []),
    ],
    [nudgesData, questionnairesData]
  )

  const scheduleById = useMemo(() => {
    const map = new Map<number, ScheduleItem>()
    for (const item of rawSchedule) {
      map.set(item.id, item)
    }
    return map
  }, [rawSchedule])

  const loadSessions = useCallback(async () => {
    if (!includeSessions || !jwt || !practitionerId) {
      setSessions([])
      return
    }
    setSessionsLoading(true)
    try {
      const list = await fetchTranscriptSessions(jwt, {
        clientId: client.id,
        practitionerId,
      })
      setSessions(list)
    } catch {
      setSessions([])
    } finally {
      setSessionsLoading(false)
    }
  }, [client.id, includeSessions, jwt, practitionerId])

  useEffect(() => {
    void loadSessions()
  }, [loadSessions])

  const events = useMemo(() => {
    const monthStart = dayjs(month).startOf("month")
    const gridStart = monthStart.subtract(monthStart.day(), "day")
    const gridEnd = gridStart.add(41, "day")
    return [
      ...expandScheduleEvents(rawSchedule, gridStart, gridEnd),
      ...sessionEvents(sessions),
    ]
  }, [month, rawSchedule, sessions])

  const loading = loadingNudges || loadingQuestionnaires || sessionsLoading

  const closeEdit = () => setEditItem(undefined)
  const closeView = () => {
    setViewItem(undefined)
    setViewDateKey(null)
  }

  return (
    <>
      <BigCalendar
        month={month}
        onMonthChange={setMonth}
        events={events}
        loading={loading}
        onEventClick={(event) => {
          if (event.kind === "session") {
            onSessionClick?.(event.id.replace(/^session-/, ""))
            return
          }
          if (event.kind !== "nudge" && event.kind !== "assessment") {
            return
          }
          const parsed = parseScheduleEventId(event.id)
          if (!parsed) return
          const item = scheduleById.get(parsed.scheduleId)
          if (!item) return

          const editable =
            canSchedule && isScheduleOccurrenceEditable(item, parsed.dateKey)

          if (editable) {
            setEditItem(item)
          } else {
            setViewItem(item)
            setViewDateKey(parsed.dateKey)
          }
        }}
      />

      <ScheduleItemModal
        client={client}
        open={Boolean(editItem)}
        onClose={closeEdit}
        item={editItem}
        canSchedule={canSchedule}
      />

      {viewItem && viewDateKey ? (
        <ScheduleEventViewDialog
          open
          onClose={closeView}
          item={viewItem}
          dateKey={viewDateKey}
        />
      ) : null}
    </>
  )
}

export default ClientCalendar
